import React from 'react';
import { motion } from 'framer-motion';
import { Bot, Instagram, Smartphone, Award, DollarSign, Zap, Database, Receipt, Users } from 'lucide-react';
import { Card } from '../UI/Card';
import { useTranslation } from 'react-i18next';

export const OfficeBenefits: React.FC = () => {
  const { t } = useTranslation(['common', 'office']);
  const isBlack = document.documentElement.classList.contains('theme-black');
  
  const benefits = [
    {
      icon: Bot,
      title: t('office:benefits.aiAutomation.title'),
      description: t('office:benefits.aiAutomation.description')
    },
    {
      icon: Instagram,
      title: t('office:benefits.growthHacking.title'),
      description: t('office:benefits.growthHacking.description')
    },
    {
      icon: Smartphone,
      title: t('office:benefits.mobileApp.title'),
      description: t('office:benefits.mobileApp.description')
    },
    {
      icon: Award,
      title: t('office:benefits.patent.title'),
      description: t('office:benefits.patent.description')
    },
    {
      icon: DollarSign,
      title: t('office:benefits.massiveSavings.title'),
      description: t('office:benefits.massiveSavings.description')
    },
    {
      icon: Zap,
      title: t('office:benefits.ultraFastDeployment.title'),
      description: t('office:benefits.ultraFastDeployment.description')
    },
    {
      icon: Database,
      title: t('office:benefits.sharedDatabase.title'),
      description: t('office:benefits.sharedDatabase.description')
    },
    {
      icon: Receipt,
      title: t('office:benefits.billing.title'),
      description: t('office:benefits.billing.description')
    },
    {
      icon: Users,
      title: t('office:benefits.multiAccount.title'),
      description: t('office:benefits.multiAccount.description')
    }
  ];
  
  return (
    <section className={`py-20 ${isBlack ? 'bg-transparent' : 'bg-white dark:bg-gray-900'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4"
          >
            {t('office:benefits.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
          >
            {t('office:benefits.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card hover className="h-full">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-accent1/20 dark:bg-accent1/30 rounded-xl">
                    <benefit.icon className="h-8 w-8 text-accent1" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 text-center">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-justify">
                  {benefit.description}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};